import { useCallback, useEffect, useState } from "react";
import * as layout from "@/lib/layoutApi";

const message = error =>
  error?.response?.data?.detail?.message ||
  (typeof error?.response?.data?.detail === "string" ? error.response.data.detail : null) ||
  // Pydantic returns a list of field errors; show the first rather than "[object Object]".
  error?.response?.data?.detail?.[0]?.msg ||
  error?.message ||
  "Could not reach the server";

const call = async fn => {
  try {
    return { ok: true, data: await fn() };
  } catch (error) {
    return { ok: false, error: message(error) };
  }
};

/**
 * Menu combos against /api/combos.
 *
 * The backend has been complete since migration 038 and there was no screen that could
 * create one — defining a combo was API-only. Every mutation here returns the server's
 * answer; nothing is reported as saved before the call resolves.
 */
export function useCombos(token) {
  const [combos, setCombos] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const result = await call(() => layout.listCombos(token));
    if (result.ok) setCombos(result.data);
    setError(result.ok ? null : result.error);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const withReload = fn => async (...args) => {
    const result = await call(() => fn(...args));
    if (result.ok) await load();
    return result;
  };

  return {
    combos,
    error,
    loading,
    reload: load,

    fetchCombo: useCallback(id => call(() => layout.getCombo(id, token)), [token]),

    createCombo: withReload(body => layout.createCombo(body, token)),
    // Replaces the component list wholesale — that is the endpoint's contract, not a
    // patch. The dialog always sends every line it is showing.
    replaceComponents: withReload((comboId, components) =>
      layout.replaceComboComponents(comboId, components, token)
    ),
    // Soft delete. order_items reference the menu item, so a sold combo has to survive.
    deactivateCombo: withReload(comboId => layout.deactivateCombo(comboId, token)),
  };
}
